import _ from 'lodash';
import * as actionTypes from '../actions/actionTypes';
import {updateObject} from '../utility';
import utilities from '../../utilities/utilities';
import {setQueryParam, setQueryParamArray} from '../../helpers/common';
import TabsParser from '../../helpers/urlParsers/TabsParser';
import timer from '../../helpers/timer';

const getInitialOverlayFromQuery = () => {
  if (typeof window === 'undefined' || !window.location?.search) {
    return '';
  }
  const leftOverlay = new URLSearchParams(window.location.search).get('leftOverlay');
  return leftOverlay ? `show${leftOverlay.charAt(0).toUpperCase()}${leftOverlay.slice(1)}` : '';
};

const applyInitialOverlayBodyClass = (enabledLeftOverlay) => {
  if (typeof document === 'undefined' || !enabledLeftOverlay) {
    return;
  }
  ['left-overlay-enabled', utilities.cleanCssIdentifier(`${enabledLeftOverlay}-enabled`)].forEach(item =>
    document.body.classList.add(item));
};

const initialEnabledLeftOverlay = getInitialOverlayFromQuery();
applyInitialOverlayBodyClass(initialEnabledLeftOverlay);

const initialState = {
  enabledLeftOverlay: initialEnabledLeftOverlay,
  isMobile: false,
  headerUpdating: false,
  menuCollapsed: false,
  popupOpened: false,
  activeTabs: {},
  breadcrumbs: [],
  canUpdateTabs: true,
  mapStyle: 'streets',
  loading: false,
  loadingList: {},
  loadingCallbacks: [],
};

const toggleLeftOverlay = (state, action) => {
  // In case we don't need to toggle class force should be TRUE.
  const name = state.enabledLeftOverlay === action.name && !action.force ? '' : action.name;
  if (state.enabledLeftOverlay) {
    ['left-overlay-enabled', utilities.cleanCssIdentifier(`${state.enabledLeftOverlay}-enabled`)].map(item =>
      document.body.classList.remove(item));
  }
  if (name) {
    ['left-overlay-enabled', utilities.cleanCssIdentifier(`${name}-enabled`)].map(item =>
      document.body.classList.add(item));
  }
  const object = {enabledLeftOverlay: name};
  // Collect every query-param change and write them in a SINGLE history update below.
  // Doing them as separate setQueryParam calls produced intermediate URL states
  // (e.g. leftOverlay still present while activeTab was already gone) that UrlQueryApplier
  // reacted to, re-opening and then re-closing the panel — a visible flicker.
  const urlParams = [];
  if (state.enabledLeftOverlay !== 'showResults' || name !== 'showProperties') {
    object.activeTabs = {};
    // Don't mutate URL when we're restoring params on refresh (updateParam=false).
    // Only clear activeTab from URL on user-triggered URL updates.
    if (action.updateParam) {
      urlParams.push({name: 'activeTab', value: ''});
    }
  }
  if (state.enabledLeftOverlay !== name && state.enabledLeftOverlay === 'showProperties') {
    urlParams.push({name: 'propertyActiveTab', value: ''});
  }
  if (action.updateParam) {
    urlParams.push({name: 'leftOverlay', value: name.replace('show', '').toLowerCase()});
    if (!name) {
      urlParams.push({name: 'activeTab', value: ''});
    }
    object.canUpdateTabs = true;
  }
  else {
    object.canUpdateTabs = false;
  }
  if (urlParams.length) {
    setQueryParamArray(urlParams, action.updateParam || 2);
  }

  return updateObject(state, object);
};

const setActiveTab = (state, action) => {
  // @todo: investigate issue with broken url state after setActiveTab.
  const object = {
    activeTabs: updateObject(state.activeTabs, action.tab),
  };
  if (action.updateParam) {
    const tabName = action.tab[Object.keys(action.tab)[0]];
    const parser = new TabsParser();
    parser.parseToUrl(tabName);
    const [, urlParams] = parser.getResult();
    Object.entries(urlParams).forEach(([key, val]) => {
      setQueryParam(key, val, action.updateParam);
    });
    object.canUpdateTabs = true;
  }
  else {
    object.canUpdateTabs = false;
  }
  return updateObject(state, object);
};
const setMapStyle = (state, action) => {
  if (action.updateParam) {
    // Remove from url as streets is used by default.
    if (action.style === 'streets') {
      setQueryParam('mapStyle', null, action.updateParam);
    }
    else {
      setQueryParam('mapStyle', action.style, action.updateParam);
    }
  }
  return updateObject(state, {mapStyle: action.style});
};

const setLoadingState = (state, loading, type = 'default', callback = null) => {
  const loadingList = _.cloneDeep(state.loadingList);
  let loadingCallbacks = _.cloneDeep(state.loadingCallbacks);
  if (callback) {
    loadingCallbacks.push(callback);
  }

  if (loading) {
    loadingList[type] = true;
  }
  else {
    delete loadingList[type];
  }

  if (!Object.keys(loadingList).filter(k => k !== 'breadcrumbs').length) {
    loadingCallbacks.map(callbackItem => callbackItem());
    loadingCallbacks = [];
    if (timer.getInterval()) {
      // eslint-disable-next-line no-console
      console.log(timer.getInterval());
    }
  }

  return updateObject(state, {
    loadingList,
    loadingCallbacks,
    loading: !_.isEmpty(Object.keys(loadingList).filter(k => k !== 'breadcrumbs')),
  });
};

// Reducer function.
const reducer = (state = initialState, action) => {
  switch (action.type) {
    case actionTypes.TOGGLE_LEFT_OVERLAY:
      return toggleLeftOverlay(state, action);
    case actionTypes.SET_LOADING:
      return setLoadingState(state, action.loading, action.loadingType, action.callback);
    case actionTypes.SET_MOBILE:
      return updateObject(state, {isMobile: action.state});
    case actionTypes.SET_HEADER_UPDATING:
      return updateObject(state, {headerUpdating: action.state});
    case actionTypes.TOGGLE_MENU_COLLAPSE:
      return updateObject(state, {menuCollapsed: action.state});
    case actionTypes.SET_POPUP_STATE:
      return updateObject(state, {popupOpened: action.state});
    case actionTypes.SET_ACTIVE_TAB:
      return setActiveTab(state, action);
    case actionTypes.SET_MAP_STYLE: {
      return setMapStyle(state, action);
    }
    case actionTypes.SET_BREADCRUMBS: {
      return updateObject(state, {breadcrumbs: action.state});
    }
    case actionTypes.ADD_BREADCRUMB: {
      const breadCrumbsArray = _.cloneDeep(state.breadcrumbs);
      const index = breadCrumbsArray.findIndex(item => item.place_type === action.state.place_type);
      if (index > -1) {
        if (index === 0) {
          return updateObject(state, {breadcrumbs: [action.state]});
        }
        return updateObject(state, {
          breadcrumbs: [...breadCrumbsArray.slice(0, index), action.state],
        });
      }
      return updateObject(state, {breadcrumbs: [...state.breadcrumbs, action.state]});
    }
    case actionTypes.SET_DOWNLOAD_PROGRESS:
      return updateObject(state, {downloadParams: action.state});
    default:
      return state;
  }
};

export default reducer;
